import { useRef, useEffect, useState } from 'react'
import { XMarkIcon, PrinterIcon } from '@heroicons/react/24/outline'
import { settingService } from '../../services/settingService'

interface Payment {
  payment_id: number
  payment_date: string
  amount: number
  transaction_reference: string | null
  status: string
  receipt_number: string | null
  customers: {
    company_name: string | null
    first_name: string | null
    last_name: string | null
  } | null
  sales: {
    sale_number: string
  } | null
  payment_methods: {
    method_name: string
  } | null
}

interface PaymentReceiptModalProps {
  isOpen: boolean
  onClose: () => void
  payment: Payment | null
}

interface CompanySettings {
  name: string
  address: string
  phone: string
  email: string
  logo: string
}

export const PaymentReceiptModal = ({ isOpen, onClose, payment }: PaymentReceiptModalProps) => {
  const printRef = useRef<HTMLDivElement>(null)
  const [companySettings, setCompanySettings] = useState<CompanySettings>({
    name: 'Aperture Camera Store',
    address: 'Nairobi, Kenya',
    phone: '+254 700 000 000',
    email: '',
    logo: '/logo.png',
  })

  useEffect(() => {
    if (isOpen) {
      settingService.getCompanySettings().then((response) => {
        if (response.data?.data) setCompanySettings(response.data.data)
      }).catch(() => {})
    }
  }, [isOpen])

  if (!isOpen || !payment) return null

  const customerName = payment.customers
    ? (payment.customers.company_name || `${payment.customers.first_name || ''} ${payment.customers.last_name || ''}`.trim())
    : 'Walk-in Customer'

  const formatCurrency = (amount: number) =>
    `KSh ${amount.toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

  const handlePrint = () => {
    const printContent = printRef.current
    if (!printContent) return
    const printWindow = window.open('', '_blank')
    if (!printWindow) {
      alert('Please allow popups to print receipts')
      return
    }
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Payment Receipt - ${payment.receipt_number || payment.payment_id}</title>
          <style>
            @page { size: 80mm auto; margin: 0; }
            body { font-family: 'Courier New', monospace; font-size: 12px; line-height: 1.4; margin: 0; padding: 10px; width: 80mm; }
            .row { display: flex; justify-content: space-between; }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
          <script>
            window.onload = function() {
              setTimeout(function() { window.print(); window.close(); }, 200);
            };
          </script>
        </body>
      </html>
    `)
    printWindow.document.close()
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-6">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />
        <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Payment Receipt</h2>
            <div className="flex items-center gap-2">
              <button
                onClick={handlePrint}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                <PrinterIcon className="w-4 h-4" />
                Print
              </button>
              <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="p-6 overflow-auto max-h-[calc(90vh-80px)] bg-gray-50">
            <div
              ref={printRef}
              className="bg-white p-6 shadow-sm"
              style={{ fontFamily: "'Courier New', monospace", fontSize: '12px', lineHeight: '1.4' }}
            >
              <div style={{ textAlign: 'center', marginBottom: '15px' }}>
                <img
                  src={companySettings.logo}
                  alt="Logo"
                  style={{ maxWidth: '80px', height: 'auto', marginBottom: '8px' }}
                  onError={(e) => { e.currentTarget.style.display = 'none' }}
                />
                <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{companySettings.name}</div>
                <div style={{ fontSize: '10px', color: '#666' }}>
                  {companySettings.address}<br />
                  {companySettings.phone && `Tel: ${companySettings.phone}`}
                </div>
              </div>

              <div style={{ borderTop: '1px dashed #000', margin: '10px 0' }} />

              <div style={{ margin: '10px 0' }}>
                <div className="row"><span>Receipt #:</span><span>{payment.receipt_number || payment.payment_id}</span></div>
                <div className="row"><span>Date:</span><span>{new Date(payment.payment_date).toLocaleString()}</span></div>
                <div className="row"><span>Customer:</span><span>{customerName}</span></div>
                {payment.sales?.sale_number && (
                  <div className="row"><span>Sale:</span><span>{payment.sales.sale_number}</span></div>
                )}
                <div className="row"><span>Method:</span><span style={{ textTransform: 'capitalize' }}>{payment.payment_methods?.method_name?.replace('_', ' ') || 'Unknown'}</span></div>
                {payment.transaction_reference && (
                  <div className="row"><span>Reference:</span><span>{payment.transaction_reference}</span></div>
                )}
                <div className="row"><span>Status:</span><span style={{ textTransform: 'capitalize' }}>{payment.status}</span></div>
              </div>

              <div style={{ borderTop: '1px dashed #000', margin: '10px 0' }} />

              <div className="row" style={{ fontSize: '14px', fontWeight: 'bold' }}>
                <span>AMOUNT:</span>
                <span>{formatCurrency(Number(payment.amount))}</span>
              </div>

              <div style={{ textAlign: 'center', marginTop: '20px', fontSize: '10px' }}>
                --- END OF RECEIPT ---
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
